import React, { createContext, useContext, PropsWithChildren } from 'react'
import {
    BreedFieldsFragment,
    ClinicSpeciesFieldsFragment,
    FilterOp,
    PatientInfo,
    useGetClinicSpeciesByClinicQuery,
    useQueryBreedsQuery,
    useQueryClinicSpeciesQuery,
} from '~graphql/generated/graphql'

type PatientInfoContextType = {
    clinic_species: ClinicSpeciesFieldsFragment | undefined
    clinic_breed: BreedFieldsFragment | undefined
}

const PatientInfoContext = createContext<PatientInfoContextType | undefined>(
    undefined
)

export default function PatientInfoProvider({
    children,
    info,
}: PropsWithChildren<{ info?: PatientInfo }>) {
    return (
        <PatientInfoContext.Provider value={{ ...usePatientInfoValues(info) }}>
            {children}
        </PatientInfoContext.Provider>
    )
}

export function usePatientInfo() {
    const context = useContext(PatientInfoContext)
    if (context === undefined) {
        throw new Error(
            'usePatientInfo must be used within a PatientInfoProvider'
        )
    }
    return context
}

function usePatientInfoValues(info?: PatientInfo): PatientInfoContextType {
    const { data: speciesData } = useQueryClinicSpeciesQuery({
        variables: {
            where: [
                {
                    column: 'id',
                    filterOp: FilterOp.Eq,
                    value: {
                        string: info?.patient_clinic_species,
                    },
                },
            ],
        },
        skip: !info?.patient_clinic_species,
    })

    const { data: breedData } = useQueryBreedsQuery({
        variables: {
            where: [
                {
                    column: 'id',
                    filterOp: FilterOp.Eq,
                    value: {
                        string: info?.patient_clinic_breed,
                    },
                },
            ],
        },
        skip: !info?.patient_clinic_breed,
    })

    return {
        clinic_species: speciesData?.queryClinicSpecies?.at(0),
        clinic_breed: breedData?.queryBreeds?.at(0),
    }
}
